/**
 * Mock customer service — the create/update/bulk logic moved verbatim out of
 * the old customerStore, backed by the in-memory fixtures.
 */

import { mockCustomers, mockCAs } from '@/data/mockCustomers'
import { mockDelay } from '@/services/factory'
import { AUTH_STORAGE_KEYS } from '@/types/auth.types'
import { resolveRole } from '@/security/accessContract'
import type { CustomerService } from './customers.service'
import type {
  IndividualCustomer,
  CreateCustomerInput,
  UpdateCustomerInput,
  CustomerAssignmentInput,
  CustomerAssignmentEvent,
} from '@/types/customer'

function resolveCaName(caId: string): string {
  const ca = mockCAs.find(c => c.id === caId)
  return ca ? `${ca.firstName} ${ca.lastName}` : ''
}

function getCurrentAuthUser(): {
  id?: string
  role?: string
  fullName?: string
  firstName?: string
  lastName?: string
  email?: string
} | null {
  try {
    const isRemember =
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(AUTH_STORAGE_KEYS.REMEMBER_ME) === 'true'
    const storage = isRemember
      ? localStorage
      : typeof sessionStorage !== 'undefined'
        ? sessionStorage
        : null
    const storedUser =
      storage?.getItem(AUTH_STORAGE_KEYS.USER) ||
      (typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_STORAGE_KEYS.USER) : null)
    return storedUser ? JSON.parse(storedUser) : null
  } catch {
    return null
  }
}

export function createMockCustomerService(): CustomerService {
  return {
    async list(): Promise<IndividualCustomer[]> {
      await mockDelay(300)
      const user = getCurrentAuthUser()
      if (!user || !user.role) return [...mockCustomers]

      const role = resolveRole(user.role).canonicalRole
      // Firm owner, firm admin, and platform admins have tenant-wide / platform-wide access
      if (
        role === 'firm_owner' ||
        role === 'firm_admin' ||
        role === 'cml_admin' ||
        role === 'cml_operations' ||
        role === 'cml_monitor'
      ) {
        return [...mockCustomers]
      }

      // Scope to assigned staff for assistant/partner/staff roles
      const staffId = user.id || ''
      const staffName = (user.fullName || `${user.firstName || ''} ${user.lastName || ''}`)
        .trim()
        .toLowerCase()
      const staffEmail = (user.email || '').toLowerCase()

      const matchedCA = mockCAs.find(
        ca =>
          ca.id === staffId ||
          `${ca.firstName} ${ca.lastName}`.toLowerCase() === staffName ||
          ca.email.toLowerCase() === staffEmail
      )

      const scoped = mockCustomers.filter(c => {
        if (!c.assignedCA && !c.assignedCAName) return false
        if (staffId && c.assignedCA === staffId) return true
        if (staffName && c.assignedCAName && c.assignedCAName.toLowerCase() === staffName)
          return true
        if (matchedCA && c.assignedCA === matchedCA.id) return true
        return false
      })

      if (scoped.length > 0) {
        return scoped
      }

      // Fallback deterministic subset for mock persona users (e.g. mediumPartner1, mediumAssistant1)
      const seed = staffId || staffEmail || staffName || 'staff'
      const caIndex =
        Math.abs(seed.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) % mockCAs.length
      const fallbackCAId = mockCAs[caIndex]?.id || 'ca-001'
      return mockCustomers.filter(c => c.assignedCA === fallbackCAId)
    },

    async create(input: CreateCustomerInput): Promise<IndividualCustomer> {
      await mockDelay(500)
      return {
        id: `cust-${Date.now()}`,
        ...input,
        assignedCA: 'ca-001',
        assignedCAName: 'Rajesh Kumar',
        status: 'active',
        createdDate: new Date().toISOString().split('T')[0],
        lastUpdated: new Date().toISOString(),
        totalAssessments: 0,
        pendingAssessments: 0,
        overdueAssessments: 0,
        assessments: [],
      }
    },

    async update(_id: string, updates: UpdateCustomerInput): Promise<Partial<IndividualCustomer>> {
      await mockDelay(300)
      return { ...updates, lastUpdated: new Date().toISOString() }
    },

    async remove(_id: string, _reason?: string): Promise<void> {
      await mockDelay(300)
    },

    async bulkArchive(_ids: string[]): Promise<void> {
      await mockDelay(500)
    },

    async bulkDelete(_ids: string[]): Promise<void> {
      await mockDelay(500)
    },

    async bulkAssignCA(_ids: string[], caId: string): Promise<{ caId: string; caName: string }> {
      await mockDelay(500)
      return { caId, caName: resolveCaName(caId) }
    },

    async assignCustomer(
      customerId: string,
      input: CustomerAssignmentInput,
      customerType = 'individual'
    ): Promise<{
      customerType: string
      customerId: number | string
      assignment: Record<string, any>
    }> {
      await mockDelay(400)
      const customer = mockCustomers.find(c => String(c.id) === String(customerId))
      const before = customer
        ? {
            assigned_ca: customer.assignedCA,
            assigned_ca_name: customer.assignedCAName,
            reviewer_user_id: customer.reviewerUserId,
            reviewer_name: customer.reviewerName,
            researcher_user_id: customer.researcherUserId,
            researcher_name: customer.researcherName,
          }
        : {}

      if (customer) {
        if (input.assignedToUserId !== undefined) {
          customer.assignedCA = input.assignedToUserId
          customer.assignedCAName = input.assignedToName || resolveCaName(input.assignedToUserId)
        }
        if (input.reviewerUserId !== undefined) {
          customer.reviewerUserId = input.reviewerUserId
          customer.reviewerName = input.reviewerName || resolveCaName(input.reviewerUserId)
        }
        if (input.researcherUserId !== undefined) {
          customer.researcherUserId = input.researcherUserId
          customer.researcherName = input.researcherName || resolveCaName(input.researcherUserId)
        }
        customer.lastUpdated = new Date().toISOString()
      }

      const after = {
        assigned_ca: input.assignedToUserId,
        assigned_ca_name: input.assignedToName,
        reviewer_user_id: input.reviewerUserId,
        reviewer_name: input.reviewerName,
        researcher_user_id: input.researcherUserId,
        researcher_name: input.researcherName,
      }

      mockAssignmentEvents.unshift({
        id: `event-${Date.now()}`,
        tenantId: 'mock-tenant',
        customerType: customerType as 'individual' | 'business',
        customerId,
        beforeAssignment: before,
        afterAssignment: after,
        reason: input.reason,
        changedBy: 'Current User',
        createdAt: new Date().toISOString(),
      })

      return {
        customerType,
        customerId,
        assignment: after,
      }
    },

    async getAssignmentHistory(
      customerId: string,
      _customerType = 'individual'
    ): Promise<CustomerAssignmentEvent[]> {
      await mockDelay(300)
      return mockAssignmentEvents.filter(e => String(e.customerId) === String(customerId))
    },

    async resolveClientCustomerId(customerId: string): Promise<number> {
      // Seeded mock customer IDs model firm-local records; map their stable
      // numeric suffix to a synthetic client-owned ID for the cutover view.
      const seededId = /^cust-(\d{3})$/.exec(customerId)
      const ownerId = Number(seededId?.[1] ?? customerId)
      if (!Number.isSafeInteger(ownerId) || ownerId < 1 || ownerId > 2_147_483_647) {
        throw new Error('A numeric Customer ID is required in mock mode')
      }
      return ownerId
    },
  }
}

const mockAssignmentEvents: CustomerAssignmentEvent[] = []
